import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CheckCircle2, Loader2, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLead } from "../../context/LeadContext";
import { api } from "../../lib/api";

const INDUSTRIES = [
  "Real Estate",
  "E-commerce / D2C",
  "Healthcare & Clinics",
  "Education & Coaching",
  "Financial Services",
  "SaaS / Technology",
  "Automotive",
  "Travel & Hospitality",
  "Other",
];

const empty = { name: "", work_email: "", company: "", phone: "", industry: "" };

export const LeadCaptureDialog = () => {
  const { open, setOpen, prefill, unlocked, leadInfo, markUnlocked } = useLead();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState("form"); // form | done
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, ...prefill.fields });
      setErrors({});
      setStep(unlocked && !prefill.forceForm ? "done" : "form");
    }
  }, [open, prefill, unlocked]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.work_email)) e.work_email = "Enter a valid work email";
    if (!form.company.trim()) e.company = "Required";
    if (!/^[+()\-\s0-9]{7,}$/.test(form.phone)) e.phone = "Enter a valid phone";
    if (!form.industry) e.industry = "Select one";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        lead_score: prefill.leadScore ?? null,
        tag: prefill.tag ?? null,
        qualification: prefill.qualification ?? null,
        source: prefill.source || "demo_gate",
      };
      const { data } = await api.post("/leads", payload);
      markUnlocked({ ...form, id: data.id, tag: data.tag, lead_score: data.lead_score });
      setStep("done");
      toast.success("Demo unlocked, welcome aboard!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const info = step === "done" ? leadInfo || form : form;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="lead-capture-dialog"
        className="max-w-lg overflow-hidden border-silver-300/60 p-0"
      >
        <DialogTitle className="sr-only">Unlock your personalized demo</DialogTitle>
        <DialogDescription className="sr-only">
          Submit your contact details to instantly unlock a personalized Digimonk.AI demo.
        </DialogDescription>
        <AnimatePresence mode="wait">
          {step === "form" ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-7"
            >
              <div className="mb-5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-light px-3 py-1 text-xs font-bold uppercase tracking-widest text-brand-dark">
                  <Sparkles className="h-3.5 w-3.5" /> Unlock your demo
                </span>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-charcoal">
                  See your personalized AI agent
                </h3>
                <p className="mt-1 text-sm text-silver-500">
                  Tell us where to send it. Your tailored demo unlocks instantly. No calls, no waiting.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-3.5" noValidate>
                <Field label="Full name" error={errors.name}>
                  <Input
                    data-testid="lead-name-input"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jordan Lee"
                  />
                </Field>
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  <Field label="Work email" error={errors.work_email}>
                    <Input
                      data-testid="lead-email-input"
                      type="email"
                      value={form.work_email}
                      onChange={(e) => update("work_email", e.target.value)}
                      placeholder="jordan@company.com"
                    />
                  </Field>
                  <Field label="Phone" error={errors.phone}>
                    <Input
                      data-testid="lead-phone-input"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+1 555 012 3456"
                    />
                  </Field>
                </div>
                <Field label="Company" error={errors.company}>
                  <Input
                    data-testid="lead-company-input"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Acme Inc."
                  />
                </Field>
                <Field label="Industry" error={errors.industry}>
                  <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                    <SelectTrigger data-testid="lead-industry-select">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((i) => (
                        <SelectItem key={i} value={i} data-testid={`industry-${i}`}>
                          {i}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Button
                  data-testid="lead-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="group mt-1 h-12 w-full bg-brand text-base font-bold text-white hover:bg-brand-hover"
                >
                  {submitting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Unlock my personalized demo
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>
                <p className="flex items-center justify-center gap-1.5 text-xs text-silver-500">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand" /> We never share your data. Unsubscribe anytime.
                </p>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-7"
              data-testid="demo-unlocked-panel"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-light">
                  <CheckCircle2 className="h-6 w-6 text-brand-dark" />
                </span>
                <div>
                  <h3 className="font-display text-2xl font-extrabold tracking-tight text-charcoal">
                    Demo unlocked{info?.name ? `, ${info.name.split(" ")[0]}` : ""} 🎉
                  </h3>
                  <p className="text-sm text-silver-500">{`Here's what your AI agent would do for ${info?.company || "your team"}.`}</p>
                </div>
              </div>

              <div className="mt-5 space-y-2.5">
                {[
                  `Greets every ${info?.industry || "inbound"} lead on WhatsApp in under 5 seconds, 24/7`,
                  "Asks industry-specific qualifying questions & scores intent in real time",
                  "Books the hot ones straight into your calendar and syncs to your CRM",
                  "Nudges the warm ones with adaptive follow-ups until they convert",
                ].map((t, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 * i }}
                    className="flex items-start gap-2.5 rounded-xl border border-silver-300/60 bg-silver-100/60 px-4 py-3"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span className="text-sm font-medium text-silver-700">{t}</span>
                  </motion.div>
                ))}
              </div>

              <Button
                data-testid="demo-close-btn"
                onClick={() => setOpen(false)}
                className="mt-6 h-12 w-full bg-charcoal text-base font-bold text-white hover:bg-charcoal/90"
              >
                Try the live AI simulator
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-silver-700">
      {label}
    </Label>
    {children}
    {error && <p className="mt-1 text-xs font-medium text-red-500">{error}</p>}
  </div>
);

export default LeadCaptureDialog;
